/* eslint-disable react-hooks/rules-of-hooks */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  BoxExtendedProps,
  costFormater,
  Enrollment,
  Hint,
  InfoOutlineIcon,
  InvestmentCard,
  ShimmerBox,
  Text,
} from '@common-fe/common-fe';

import { useInvestmentsHoldings, useInvestor } from '../hooks';

interface Props extends BoxExtendedProps {
  enrollment: Enrollment;
}
const InvestmentCardBlock: React.FC<Props> = ({ enrollment, ...rest }) => {
  if (!enrollment) {
    return null;
  }
  const { t } = useTranslation();
  const {
    details: { portfolioBalance = 0 },

    isHoldingLoading,
  } = useInvestmentsHoldings(enrollment.id);
  const { data: investor, isLoading } = useInvestor();

  const currentType = useMemo(() => {
    return enrollment.accountType;
  }, [enrollment]);

  const availableBalance = useMemo(() => {
    return enrollment?.available - enrollment?.investmentThreshold > 0
      ? enrollment?.available - enrollment?.investmentThreshold
      : 0;
  }, [enrollment?.available, enrollment?.investmentThreshold]);
  if (isHoldingLoading || isLoading) {
    return (
      <InvestmentCard width={{ min: '400px', max: '400px' }} height="248px" {...rest} type={currentType}>
        <InvestmentCard.Icon type={currentType} />
        <InvestmentCard.Title truncate title={enrollment.name}>
          {enrollment.name}
        </InvestmentCard.Title>
        <Box margin={{ top: 'spacing2' }} direction="row" height="24px" justify="between">
          <ShimmerBox height="24px" round="2px" width="50%" />
          <ShimmerBox height="24px" round="2px" width="20%" />
        </Box>
        <Box margin={{ top: 'spacing2' }} direction="row" height="24px" justify="between">
          <ShimmerBox height="24px" round="2px" width="35%" />
          <ShimmerBox height="24px" round="2px" width="15%" />
        </Box>
        <Box margin={{ top: 'spacing2' }} direction="row" height="24px" justify="between">
          <ShimmerBox height="24px" round="2px" width="32%" />
          <ShimmerBox height="24px" round="2px" width="25%" />
        </Box>
      </InvestmentCard>
    );
  }
  return (
    <InvestmentCard
      width={{ min: '400px', max: '400px' }}
      style={{ position: 'relative' }}
      height="248px"
      {...rest}
      type={currentType}
    >
      <InvestmentCard.Icon type={currentType} />
      <InvestmentCard.Title truncate title={enrollment.name}>
        {enrollment.name}
      </InvestmentCard.Title>

      <InvestmentCard.Item
        height={{ min: '24px' }}
        margin={{ top: 'spacing2' }}
        data-testId="investment-card-block_portfolio-balance"
      >
        <InvestmentCard.Item.Text data-testId="investment-card-block_portfolio-balance_label">
          Investment portfolio balance
        </InvestmentCard.Item.Text>
        <InvestmentCard.Item.Amount size="large" data-testId="investment-card-block_portfolio-balance_amount">
          {costFormater(portfolioBalance, {
            isOptionalPrecision: true,
          })}
        </InvestmentCard.Item.Amount>
      </InvestmentCard.Item>
      {!!investor.cashBalance && (
        <InvestmentCard.Item
          height={{ min: '24px' }}
          margin={{ top: 'spacing2' }}
          align="center"
          data-testId="investment-card-block_cash-balance"
        >
          <InvestmentCard.Item.Text data-testId="investment-card-block_cash-balance_label">
            Available cash balance
          </InvestmentCard.Item.Text>

          <Box direction="row" align="center">
            {!!enrollment?.investmentThreshold && (
              <Hint
                align="left"
                hintWidth="150px"
                hintElement={<InfoOutlineIcon color="iconSecondary" size="16px" />}
              >
                <Text size="small" color="textOnColor">
                  {`${t('Required minimum balance:')} ${costFormater(enrollment?.investmentThreshold, {
                    isOptionalPrecision: true,
                  })}`}
                </Text>
              </Hint>
            )}

            <InvestmentCard.Item.Amount size="large" data-testId="investment-card-block_cash-balance_amount">
              {costFormater(investor.cashBalance, {
                isOptionalPrecision: true,
              })}
            </InvestmentCard.Item.Amount>
          </Box>
        </InvestmentCard.Item>
      )}

      <InvestmentCard.Item
        height={{ min: '24px' }}
        margin={{ top: 'spacing2' }}
        data-testId="investment-card-block_available-balance"
      >
        <InvestmentCard.Item.Text data-testId="investment-card-block_available-balance_label">
          Available balance to invest
        </InvestmentCard.Item.Text>
        <InvestmentCard.Item.Amount data-testId="investment-card-block_available-balance_amount">
          {costFormater(availableBalance, {
            isOptionalPrecision: true,
          })}
        </InvestmentCard.Item.Amount>
      </InvestmentCard.Item>
    </InvestmentCard>
  );
};

export default InvestmentCardBlock;
